import * as Sentry from "@sentry/react";

type LogLevel = "info" | "warn" | "error" | "debug";

export interface LogContext {
  tags?: Record<string, string | number | boolean | undefined>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

export class Logger {
  private moduleName: string;

  constructor(moduleName: string) {
    this.moduleName = moduleName;
  }

  private getTimestamp(): string {
    return new Date().toLocaleTimeString(undefined, {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  private log(level: LogLevel, message: string, ...args: any[]) {
    const timestamp = this.getTimestamp();
    const config = this.getLevelConfig(level);

    const prefix = `%c${config.icon} [${timestamp}] [${this.moduleName}]`;
    const style = `color: ${config.color}; font-weight: bold;`;

    console[level](prefix, style, message, ...args);
  }

  private getLevelConfig(level: LogLevel) {
    switch (level) {
      case "info":
        return { icon: "ℹ️", color: "#3b82f6" }; // Blue
      case "warn":
        return { icon: "⚠️", color: "#eab308" }; // Yellow
      case "error":
        return { icon: "🛑", color: "#ef4444" }; // Red
      case "debug":
        return { icon: "🔍", color: "#6b7280" }; // Grey
      default:
        return { icon: "📝", color: "#000000" };
    }
  }

  info(message: string, ...args: any[]) {
    this.log("info", message, ...args);
  }

  warn(message: string, ...args: any[]) {
    this.log("warn", message, ...args);
  }

  /**
   * Journalise une erreur en console **et** l'envoie à Sentry.
   *
   * @param context Scope Sentry optionnel. Les `tags` sont filtrables/agrégeables dans
   *   l'UI Sentry ; le `fingerprint` contrôle le regroupement en issues — sans lui, tous
   *   les appels API échoués se regroupent par message et finissent dans une seule issue
   *   fourre-tout, ce qui rend le diagnostic impossible.
   * @returns L'id de l'événement Sentry, à afficher comme référence à l'utilisateur.
   */
  error(message: string, error?: unknown, context?: LogContext): string | undefined {
    this.log("error", message, ...(error === undefined ? [] : [error]));

    // On capture aussi les valeurs non-Error (objets de diagnostic) : elles étaient
    // auparavant silencieusement perdues alors qu'elles signalent de vrais problèmes
    // (ex. une réponse d'API dont la forme ne correspond plus au schéma attendu).
    const exception = error instanceof Error ? error : new Error(`[${this.moduleName}] ${message}`);

    return Sentry.captureException(exception, {
      tags: { module: this.moduleName, ...context?.tags },
      extra: {
        ...(error instanceof Error ? {} : { value: error }),
        ...context?.extra,
      },
      fingerprint: context?.fingerprint,
    });
  }

  /**
   * Journalise un échec dont la remontée a **peut-être déjà eu lieu** ailleurs — ou dont on a
   * délibérément décidé qu'elle n'aurait pas lieu.
   *
   * `fetchJson` capture chaque échec d'API dans Sentry — méthode, route normalisée, statut, une
   * issue par endpoint — puis pose l'`eventId` obtenu sur l'erreur qu'il propage. Un `error()` chez
   * l'appelant produit alors un **second** événement pour la même cause, avec moins de contexte que
   * l'original. Le motif est revenu trois fois cette semaine, à trois endroits sans rapport : le
   * signal de sortie de séance, la demande de prise de parole, le re-join après reconnexion.
   *
   * La présence d'un `eventId` est le signal exact de « déjà rapporté », et il vaut mieux qu'un test
   * de type : un 401 traité comme une expiration normale n'en porte pas, et reste donc signalé si un
   * appelant le considère comme un vrai échec.
   *
   * `silenced` couvre le cas symétrique : la couche API a jugé l'échec inintéressant (page qui s'en
   * va, appareil hors ligne) et n'a produit **aucun** événement. Sans lui, ce silence-là serait lu
   * comme « pas encore signalé » et l'appelant recréerait exactement le bruit qu'on venait d'éviter.
   *
   * Détection par la forme plutôt que par `instanceof ApiError` : la couche API importe ce module,
   * l'inverse créerait un cycle.
   *
   * @returns L'`eventId` existant s'il y en a un, sinon celui du nouvel événement — `undefined`
   *   quand l'échec était volontairement tu.
   */
  failure(message: string, error?: unknown, context?: LogContext): string | undefined {
    const record =
      typeof error === "object" && error !== null ? (error as Record<string, unknown>) : undefined;
    const existing = record?.eventId;
    if (typeof existing === "string") {
      this.warn(message, error);
      return existing;
    }
    if (record?.silenced === true) {
      this.warn(message, error);
      return undefined;
    }
    return this.error(message, error, context);
  }

  debug(message: string, ...args: any[]) {
    if (import.meta.env.DEV) {
      this.log("debug", message, ...args);
    }
  }
}
